import { createTheme, type ThemeOptions, type PaletteMode } from '@mui/material/styles';

const BARWOOD = {
  bg: '#f2f0e8',
  ink: '#17343a',
  card: '#fffdf8',
  accent: '#1f6f78',
  accentSoft: '#d3ebe8',
  accentDark: '#145058',
  border: '#d9d1c2',
  brandGreen: '#6fa240',
  muted: '#6b7a80',
};

const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: {
            main: BARWOOD.accent,
            light: '#2f8a93',
            dark: BARWOOD.accentDark,
            contrastText: '#ffffff',
          },
          secondary: {
            main: BARWOOD.brandGreen,
            light: '#8bbd5f',
            dark: '#527d2f',
            contrastText: '#ffffff',
          },
          background: {
            default: BARWOOD.bg,
            paper: BARWOOD.card,
          },
          text: {
            primary: BARWOOD.ink,
            secondary: BARWOOD.muted,
          },
          divider: BARWOOD.border,
          success: { main: '#1a6b3c' },
          error: { main: '#8b1a1a' },
          warning: { main: '#b4781a' },
          info: { main: BARWOOD.accent },
        }
      : {
          primary: {
            main: '#2f8a93',
            light: '#4aa3ad',
            dark: BARWOOD.accent,
            contrastText: '#ffffff',
          },
          secondary: {
            main: '#8bbd5f',
            light: '#a6d27c',
            dark: BARWOOD.brandGreen,
            contrastText: '#ffffff',
          },
          background: {
            default: '#0f1f23',
            paper: '#17343a',
          },
          text: {
            primary: '#f1f5f4',
            secondary: '#94a3a8',
          },
          divider: '#2a4a52',
          success: { main: '#2e9157' },
          error: { main: '#c94a4a' },
          warning: { main: '#d99938' },
          info: { main: '#2f8a93' },
        }),
  },
  typography: {
    fontFamily: [
      'Aptos',
      '"Segoe UI"',
      '-apple-system',
      'BlinkMacSystemFont',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    fontSize: 13,
    htmlFontSize: 16,
    h1: { fontWeight: 700, fontSize: '1.75rem', letterSpacing: '-0.01em' },
    h2: { fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.01em' },
    h3: { fontWeight: 700, fontSize: '1.25rem', letterSpacing: '-0.01em' },
    h4: { fontWeight: 700, fontSize: '1.15rem', letterSpacing: '-0.01em' },
    h5: { fontWeight: 700, fontSize: '1rem' },
    h6: { fontWeight: 700, fontSize: '0.9rem' },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8125rem' },
    subtitle1: { fontSize: '0.875rem' },
    subtitle2: { fontSize: '0.8125rem' },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.8125rem',
      letterSpacing: '0.01em',
    },
    caption: { fontSize: '0.75rem' },
    overline: { fontSize: '0.7rem', letterSpacing: '0.08em' },
  },
  shape: {
    borderRadius: 2,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: mode === 'light' ? BARWOOD.bg : '#0f1f23',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          padding: '6px 12px',
          fontWeight: 600,
        },
        sizeSmall: {
          padding: '4px 10px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          backgroundColor: BARWOOD.accent,
          '&:hover': { backgroundColor: BARWOOD.accentDark },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          border: `1px solid ${mode === 'light' ? BARWOOD.border : '#2a4a52'}`,
          boxShadow:
            mode === 'light'
              ? '0 2px 12px rgba(23,52,58,0.05)'
              : '0 2px 12px rgba(0,0,0,0.3)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundColor: mode === 'light' ? BARWOOD.ink : '#0b1a1e',
          color: '#ffffff',
          borderBottom: 'none',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 56,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
        fullWidth: true,
        InputLabelProps: { shrink: true },
      },
      styleOverrides: {
        root: {
          maxWidth: 480,
          '&[data-wide="true"]': {
            maxWidth: '100%',
          },
          '& .MuiFormHelperText-root': {
            marginLeft: 2,
            marginTop: 4,
            fontSize: '0.7rem',
          },
          '&:has(input[type="number"])': {
            maxWidth: 200,
          },
          '&:has(input[type="password"])': {
            maxWidth: 360,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          backgroundColor: mode === 'light' ? '#ffffff' : 'rgba(255,255,255,0.04)',
          fontSize: '0.875rem',
          lineHeight: 1.4,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: mode === 'light' ? BARWOOD.border : '#2a4a52',
            top: 0,
            '& legend': {
              display: 'none',
            },
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: BARWOOD.accent,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: BARWOOD.accent,
            borderWidth: 1,
          },
        },
        input: {
          padding: '9px 12px',
          height: '1.4em',
          '&::placeholder': {
            opacity: 0.7,
          },
          '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
            {
              WebkitBoxShadow: `0 0 0 100px ${mode === 'light' ? '#ffffff' : '#17343a'} inset`,
              WebkitTextFillColor: mode === 'light' ? BARWOOD.ink : '#f1f5f4',
              caretColor: mode === 'light' ? BARWOOD.ink : '#f1f5f4',
              transition: 'background-color 5000s ease-in-out 0s',
            },
        },
        inputSizeSmall: {
          padding: '8px 12px',
          height: '1.4em',
        },
        multiline: {
          padding: 0,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        input: {
          padding: '9px 12px',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
        inputMultiline: {
          padding: '9px 12px',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.72rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: mode === 'light' ? BARWOOD.muted : '#94a3a8',
          position: 'static',
          transform: 'none',
          marginBottom: 6,
          pointerEvents: 'auto',
          maxWidth: '100%',
          whiteSpace: 'normal',
          overflow: 'visible',
          textOverflow: 'clip',
          lineHeight: 1.3,
          '&.Mui-focused': {
            color: mode === 'light' ? BARWOOD.muted : '#94a3a8',
          },
          '&.MuiInputLabel-shrink': {
            transform: 'none',
          },
          '&.MuiInputLabel-sizeSmall': {
            transform: 'none',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-outlined': {
            position: 'static',
            transform: 'none',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${mode === 'light' ? BARWOOD.border : '#2a4a52'}`,
          minHeight: 44,
        },
        indicator: {
          backgroundColor: BARWOOD.accent,
          height: 2,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.9rem',
          minHeight: 44,
          padding: '10px 16px',
          color: mode === 'light' ? BARWOOD.muted : '#94a3a8',
          '&.Mui-selected': {
            color: BARWOOD.accent,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: mode === 'light' ? BARWOOD.accentSoft : 'rgba(47,138,147,0.18)',
            color: mode === 'light' ? BARWOOD.accentDark : '#f1f5f4',
            '&:hover': {
              backgroundColor: mode === 'light' ? BARWOOD.accentSoft : 'rgba(47,138,147,0.24)',
            },
            '& .MuiListItemIcon-root': {
              color: mode === 'light' ? BARWOOD.accentDark : '#f1f5f4',
            },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: mode === 'light' ? BARWOOD.border : '#2a4a52',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 24,
        },
        label: {
          paddingLeft: 8,
          paddingRight: 8,
        },
        sizeSmall: {
          height: 22,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          borderRadius: 3,
        },
      },
    },
  },
});

export const createAppTheme = (mode: PaletteMode) => {
  return createTheme(getDesignTokens(mode));
};

export default createAppTheme('light');
